import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ROLE, type Role } from '@ovrsee/shared'
import { useAuth } from '../../lib/auth.js'

export interface NotificationItem {
  readonly id: string
  readonly type: string
  readonly title: string
  readonly taskId: string | null
  readonly submissionId: string | null
  readonly leaveRequestId: string | null
  readonly createdAt: string
  readonly readAt: string | null
  readonly actor: { readonly id: string; readonly name: string } | null
  readonly task: { readonly id: string; readonly reference: string; readonly title: string } | null
}

export interface NotificationPage {
  readonly items: readonly NotificationItem[]
  readonly total: number
  readonly page: number
  readonly pageSize: number
}

/** Spec §2.8: 60-second poll plus window-focus refetch. */
export const UNREAD_POLL_MS = 60_000

export function useUnreadCount() {
  const { api } = useAuth()
  return useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () => api.get<{ count: number }>('/api/notifications/unread-count'),
    refetchInterval: UNREAD_POLL_MS,
    // The client default staleTime (30s) would make a focus refetch a no-op for
    // half of every poll interval; a count this cheap is always worth refreshing.
    staleTime: 0,
  })
}

export function useNotificationList(enabled: boolean) {
  const { api } = useAuth()
  return useQuery({
    queryKey: ['notifications', 'list'],
    queryFn: () => api.get<NotificationPage>('/api/notifications?pageSize=10'),
    enabled,
  })
}

export function useMarkRead() {
  const { api } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.post<{ ok: boolean }>(`/api/notifications/${encodeURIComponent(id)}/read`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['notifications'] })
    },
  })
}

export function useMarkAllRead() {
  const { api } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<{ ok: boolean }>('/api/notifications/read-all'),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['notifications'] })
    },
  })
}

/**
 * Spec §5 (decided in brainstorm): managers land on the Tasks screen with the
 * task's modal open; employees land on My Day with the task's card highlighted.
 */
export function deepLinkFor(role: Role, taskId: string): string {
  return role === ROLE.EMPLOYEE
    ? `/my-day?task=${encodeURIComponent(taskId)}`
    : `/tasks?task=${encodeURIComponent(taskId)}`
}

/**
 * Where a bell row goes. A submission notification carries no task (spec §2.5),
 * so it lands on the innovation screen instead of a task deep link.
 */
export function notificationLinkFor(
  role: Role,
  item: Pick<NotificationItem, 'taskId' | 'submissionId' | 'leaveRequestId'>,
): string {
  if (item.submissionId !== null) return '/innovation'
  if (item.leaveRequestId !== null) return `/leave?leave=${encodeURIComponent(item.leaveRequestId)}`
  return item.taskId === null ? '/' : deepLinkFor(role, item.taskId)
}
